import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { ResponsiveLine } from '@nivo/line';
import { useData } from 'context/data.context';
import { useProfile } from 'context/profile.context';
import { CHART_THEME, CLASSES, CLASS_COLOUR_MAP, CLASS_LABELS } from 'utilities/global.variables';
import { formatCurrency, formatCurrencyCompact } from 'utilities/global.functions';

// Net Worth line plus every classification, in legend and stacking order
const SERIES_KEYS = ['Net Worth', ...CLASSES];

// Vertical dotted line at the year the user reaches their target retirement age. The x scale is
// a point scale over the timeline's years, so an arbitrary target year isn't in its domain — the
// position is interpolated between the two years bracketing it.
const RetirementAgeLine = ({ xScale, innerHeight, innerWidth, targetYear, targetAge, years }) => {
  if (!targetYear || !years.length) return null;

  const first = Number(years[0]);
  const last = Number(years[years.length - 1]);
  if (targetYear < first || targetYear > last) return null; // outside the forecast horizon

  const next = years.findIndex(y => Number(y) >= targetYear);
  let x;
  if (Number(years[next]) === targetYear) {
    x = xScale(years[next]);
  } else {
    const y0 = Number(years[next - 1]);
    const y1 = Number(years[next]);
    const x0 = xScale(years[next - 1]);
    const x1 = xScale(years[next]);
    x = x0 + (x1 - x0) * ((targetYear - y0) / (y1 - y0));
  }

  // Flip the label to the left of the line when it wouldn't fit to the right.
  // ~6.5px per character approximates the label width at the chart's font size.
  const label = `Target age (${targetAge})`;
  const atEnd = x > innerWidth - (label.length * 6.5);

  return (
    <g>
      <line
        x1={x} x2={x} y1={0} y2={innerHeight}
        stroke="var(--networth)" strokeWidth={1} strokeDasharray="4 4" strokeOpacity={0.6}
      />
      <text
        transform={`translate(${atEnd ? x + 8 : x - 8}, 72) rotate(${atEnd ? 90 : -90})`}
        textAnchor="start"
        fill="var(--l4)" fontSize="1em" fontFamily="inherit"
      >
        {label}
      </text>
    </g>
  );
};

const NetWorthArea = ({ series, yScale }) => {
  const networthSeries = series.find(s => s.id === 'Net Worth');
  if (!networthSeries) return null;

  const points = networthSeries.data.filter(d => d.position.x !== null && d.position.y !== null);
  if (points.length === 0) return null;

  const baseline = yScale(0);
  const top = points.map(d => `${d.position.x},${d.position.y}`).join(' L ');
  const areaPath = `M ${top} L ${points[points.length - 1].position.x} ${baseline} L ${points[0].position.x} ${baseline} Z`;

  return (
    <path d={areaPath} fill="var(--networth)" fillOpacity={0.05} stroke="none" />
  );
};

const LineProjectionCard = () => {
  const { networthProjected, loadingUserData } = useData();
  const { targetNetworth, targetAge, birthYear } = useProfile();
  const data = networthProjected.timeline || [];

  // Nivo's responsive wrapper needs a measured container, so fade in after mount
  const [mounted, setMounted] = useState(false);
  useEffect(() => { setMounted(true); }, []);

  // Start with only Net Worth plotted — every classification tag begins toggled off
  const [visibleCategories, setVisibleCategories] = useState(
    Object.fromEntries(SERIES_KEYS.map(k => [k, k === 'Net Worth']))
  );

  const toggleCategory = (category) => {
    setVisibleCategories(prev => ({ ...prev, [category]: !prev[category] }));
  };

  // One series per key; classifications the user doesn't hold are dropped entirely
  const allSeries = useMemo(() =>
    SERIES_KEYS.map(key => ({
      id: key,
      color: CLASS_COLOUR_MAP[key],
      data: data.map(d => ({ x: d.year, y: d[key] || 0 })),
    })).filter(series => series.data.some(p => p.y !== 0)),
    [data]
  );

  // Keys with at least one non-zero value — drives the legend tags
  const nonZeroKeys = useMemo(() => allSeries.map(s => s.id), [allSeries]);

  // Subset the user hasn't toggled off via the legend tags
  const lineData = useMemo(
    () => allSeries.filter(series => visibleCategories[series.id]),
    [allSeries, visibleCategories]
  );

  // Y-axis range based on visible lines only. Zero is always in the domain.
  const { minValue, maxValue } = useMemo(() => {
    const allValues = lineData.flatMap(c => c.data.map(p => p.y));
    const max = allValues.length > 0 ? Math.max(...allValues) : 0;
    return {
      minValue: allValues.length > 0 ? Math.min(...allValues, 0) : 0,
      maxValue: max,
    };
  }, [lineData]);

  // Only show the target marker when the Net Worth line is visible and the target
  // value falls within the natural data range (i.e. the projection reaches it).
  const networthVisible = lineData.some(series => series.id === 'Net Worth');
  const showTarget = networthVisible && targetNetworth <= maxValue;

  // The year the user reaches their target retirement age — needs a birth year to be knowable
  const timelineYears = useMemo(() => data.map(d => d.year), [data]);

  // Every other year, so the labels don't crowd. A point scale has no `.ticks()`, so Nivo
  // ignores a numeric `tickValues` and falls back to the whole domain — it needs an array.
  const tickYears = useMemo(() => timelineYears.filter((_, i) => i % 2 === 0), [timelineYears]);
  const targetYear = birthYear ? birthYear + targetAge : null;

  // Stable layer identity so toggling legend tags doesn't remount it
  const retirementAgeLayer = useCallback(
    (props) => (
      <RetirementAgeLine {...props} targetYear={targetYear} targetAge={targetAge} years={timelineYears} />
    ),
    [targetYear, targetAge, timelineYears]
  );


  if (loadingUserData || !allSeries.length) return null;

  return (
    <div className='Projection'>
      <div className="
       no-bg r1-1">
        {/* Line chart */}
        <div style={{ width: '100%', height: '100%', opacity: mounted ? 1 : 0, transition: 'opacity 0.25s ease' }}>
          {mounted && <ResponsiveLine
            data={lineData}
            margin={{ top: 20, right: 60, bottom: 30, left: 34 }}
            xScale={{ type: 'point' }}
            yScale={{ type: 'linear', min: minValue, max: maxValue, clamp: true }}
            markers={showTarget ? [{
              axis: 'y',
              value: targetNetworth,
              legend: `Target networth ${formatCurrencyCompact(targetNetworth)}`,
              legendPosition: 'top-left',
              legendOrientation: 'horizontal',
              lineStyle: { stroke: 'var(--networth)', strokeWidth: 1, strokeDasharray: '4 4', strokeOpacity: 0.6 },
              textStyle: { fill: 'var(--l4)', fontSize: '1em', fontFamily: 'inherit' },
            }] : []}
            theme={{
              ...CHART_THEME,
              axis: {
                ...CHART_THEME.axis,
                ticks: {
                  ...CHART_THEME.axis.ticks,
                  // Slightly larger than the shared theme's 1em, for the year labels
                  text: { ...CHART_THEME.axis.ticks.text, fontSize: '1.15em' },
                },
              },
              legends: { text: { fill: 'var(--l3)' } },
              points: {
                text: { fill: 'var(--l3)' },
                opacity: 0.5,
                borderOpacity: 0.5
              }
            }}
            curve="monotoneX"
            yFormat=" >-,.0f"
            axisBottom={{ tickSize: 0, tickPadding: 10, tickRotation: 0, tickValues: tickYears }}
            axisLeft={null}
            axisTop={null}
            axisRight={{
              tickSize: 0,
              tickPadding: 10,
              tickRotation: 0,
              tickValues: 3,
              legendOffset: 0,
              format: formatCurrencyCompact
            }}
            enableArea={false}
            areaOpacity={0.1}
            enableGridX={true}
            enableGridY={false}
            colors={{ datum: 'color' }}
            lineWidth={2}
            enablePoints={true}
            pointBorderWidth={2}
            pointSize={4}
            useMesh={true}
            enableCrosshair={true}
            enableTouchCrosshair={true}
            crosshairType="x"
            layers={['grid', NetWorthArea, 'axes', 'lines', ...(showTarget ? [retirementAgeLayer] : []), 'points', 'mesh', 'markers', 'crosshair', 'legends']}
            tooltip={({ point }) => (
              <div className="Tooltip" style={{ background: 'var(--d3)', padding: '10px', color: 'var(--l3)', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)', width: 'max-content' }}>
                <div className='flex col medium-gap'>
                  <div className="flex row space large-gap">
                    <p className='regular'>{CLASS_LABELS[point.seriesId] ?? point.seriesId}</p>
                    <p className='regular o60'>{point.data.x}</p>
                  </div>
                  <div>
                    <p className="number xlarge">{formatCurrency(point.data.y)}</p>
                  </div>
                </div>
              </div>
            )}
          />}
        </div>
      </div>

      <div className='card  no-bg'>
        {/* Category filter tags */}
        <div className="flex row wrap medium-gap" style={{ justifyContent: 'center' }}>
          {nonZeroKeys.map(key => (
            <div
              key={key}
              className={`tag projection flex row center medium-gap ${!visibleCategories[key] ? 'hidden' : ''}`}
              onClick={() => toggleCategory(key)}
            >
              <div className="dot" data-cls={key}></div>
              <p className='regular'>{CLASS_LABELS[key] ?? key}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default LineProjectionCard;
